import { ReactNode, useCallback, useMemo } from "react";
import Form from "react-bootstrap/Form";

import { get } from "lodash";
import { Control, UseFormGetValues, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { FiX } from "react-icons/fi";
import LobbieCheckboxV2 from "src/components/shared/LobbieCheckboxV2";
import FormFieldError from "src/components/shared/forms/FormFieldError";
import HelpTooltip from "src/components/shared/tooltips/HelpTooltip";
import EmailFormController from "src/components/shared/useForm/EmailFormController";
import LobbieSelectForm from "src/components/shared/useForm/LobbieSelectForm";
import PhoneFormController from "src/components/shared/useForm/PhoneFormController";
import UseFormTypedDate from "src/components/shared/useForm/UseFormTypedDate";
import { EUserType, IS_MOBILE_PHONE } from "src/constants";
import {
    ENotificationSendMethod,
    isSendMethodEmail,
    isSendMethodSMS,
} from "src/constants/notifications";
import { useLobbieAccountRedux } from "src/hooks/accounts/accounts";
import { useUserExists } from "src/hooks/staff/patients";
import { isNumeric } from "src/utils";
import { FIELDS, IField } from "./constants";

interface IProps {
    control: Control<any>;
    register: UseFormRegister<any>;
    getValues: UseFormGetValues<any>;
    setValue: UseFormSetValue<any>;
    errors: Record<string, any> | undefined;
    searchPatientName: string | undefined;
    isChildAccountPatient: boolean;
    isRepresentative?: boolean;
    setExistingPatient?: (exist: boolean) => void;
    handleBack?: () => void;
    userType: EUserType;
    title?: ReactNode;
    patientSchedulingEmail?: string | null;
    patientSchedulingPhone?: string | null;
    verificationMethod: ENotificationSendMethod;
}

const NewPatientFormFields: React.FC<IProps> = ({
    control,
    register,
    getValues,
    setValue,
    errors,
    searchPatientName,
    isChildAccountPatient,
    isRepresentative,
    setExistingPatient,
    handleBack,
    userType,
    title,
    patientSchedulingEmail,
    patientSchedulingPhone,
    verificationMethod,
}) => {
    const isPatient = useMemo(() => userType === EUserType.Patient, [userType]);
    const {
        patientFieldName,
        lowerPatientFieldName,
        account: { verifyDOBOnFormsOpen },
    } = useLobbieAccountRedux();

    const [patientExists, getPatientExistsByEmail] = useUserExists();

    const inputPatientName = useMemo(() => searchPatientName?.split(" ", 2), [searchPatientName]);

    const isHideFromPatientScheduling = useCallback(
        (field: IField) => {
            return (
                (patientSchedulingEmail || patientSchedulingPhone) &&
                [
                    verifyDOBOnFormsOpen ? "" : "dateOfBirth",
                    "sexAssignedAtBirth",
                    "gender",
                    "legalSex",
                ].includes(field.name)
            );
        },
        [verifyDOBOnFormsOpen, patientSchedulingEmail, patientSchedulingPhone],
    );

    const withFieldPrefix = useCallback(
        (field: { name: string }) => {
            if (isRepresentative) {
                return `parent.${field.name}`;
            } else {
                return field.name;
            }
        },
        [isRepresentative],
    );

    const getErrorMessage = useCallback(
        (field: IField) => {
            if (field.name)
                return (get(errors, `${withFieldPrefix(field)}.message`) || "").replace(
                    withFieldPrefix(field),
                    field.title,
                );
        },
        [errors, withFieldPrefix],
    );

    const getExistingPatientMessage = useCallback(
        (field: IField) => {
            if (isPatient) {
                if (field.name === "email" && patientExists.includes("email")) {
                    return "Please try different email address.";
                }
                if (field.name === "mobilePhone" && patientExists.includes("phone")) {
                    return "Please try a different phone number.";
                }
            } else {
                if (field.name === "email" && patientExists.includes("email")) {
                    return `A ${lowerPatientFieldName} with this email address already exists. Changed to update.`;
                }
                if (field.name === "mobilePhone" && patientExists.includes("phone")) {
                    return `A ${lowerPatientFieldName} with this phone number already exists.`;
                }
            }
        },
        [patientExists, isPatient, lowerPatientFieldName],
    );

    const getDefaultValue = useCallback(
        (field: IField) => {
            const fieldName = withFieldPrefix(field);
            if (inputPatientName) {
                if (!control.getFieldState(fieldName).isTouched) {
                    if (field.name === "firstName") {
                        const first = inputPatientName.first() || "";
                        setValue(withFieldPrefix(field), first);
                        return first;
                    }
                    if (field.name === "lastName") {
                        const last =
                            inputPatientName.length > 1 ? inputPatientName.last() || "" : "";
                        setValue(withFieldPrefix(field), last);
                        return last;
                    }
                }
            }
            return field.defaultValue;
        },
        [inputPatientName, control, setValue, withFieldPrefix],
    );

    const getFieldTitle = useCallback((field: IField, required: boolean) => {
        if (required) {
            return `${field.title} *`;
        }
        return `${field.title} (Optional)`;
    }, []);

    const getFieldRequired = useCallback(
        (field: IField) => {
            if (["firstName", "lastName"].includes(field.name)) {
                return true;
            }
            if (field.name === "dateOfBirth" && verifyDOBOnFormsOpen) {
                return true;
            }
            if (field.name === "mobilePhone" || field.name === "email") {
                if (isNumeric(verificationMethod)) {
                    return (
                        (field.name === "email" && isSendMethodEmail(verificationMethod)) ||
                        (field.name === "mobilePhone" && isSendMethodSMS(verificationMethod))
                    );
                } else {
                    return field.name === "email" && isPatient;
                }
            } else {
                return false;
            }
        },
        [isPatient, verifyDOBOnFormsOpen, verificationMethod],
    );

    const getLabel = useCallback(
        (field: IField, required: boolean) => {
            if (isHideFromPatientScheduling(field)) return null;

            if (field.name === "email" && !required && !isNumeric(verificationMethod)) {
                return (
                    <HelpTooltip
                        tooltipId="newPatientEmailTooltip"
                        message={
                            isPatient ? (
                                "The email address at which notifications regarding this, and future appointments, will be received."
                            ) : (
                                <span className="row">
                                    <span className="col">
                                        <span className="row g-0 mb-2">
                                            Because email is not required for new
                                            {lowerPatientFieldName}s, if this field remains empty
                                            Lobbie will assign a temporary, randomized and inactive
                                            @lobbie.com email address to the new{" "}
                                            {lowerPatientFieldName}.
                                        </span>
                                        <span className="row g-0">
                                            {lowerPatientFieldName}s will NOT be able to receive
                                            notifications at these inactive email addresses.
                                        </span>
                                    </span>
                                </span>
                            )
                        }
                        title={<span className="bold user-select-none">{field.title}</span>}
                    />
                );
            }
            if (field.name === "mobilePhone" && !isNumeric(verificationMethod)) {
                return (
                    <HelpTooltip
                        tooltipId="newPatientPhoneTooltip"
                        message={`${patientFieldName}s without phone numbers will not be able to receive SMS/Text Messages.`}
                        title={
                            <span className="bold user-select-none">{`${field.title} (Optional)`}</span>
                        }
                    />
                );
            }
            return <Form.Label className="bold">{getFieldTitle(field, required)}</Form.Label>;
        },
        [
            isPatient,
            getFieldTitle,
            lowerPatientFieldName,
            patientFieldName,
            isHideFromPatientScheduling,
            verificationMethod,
        ],
    );

    const getAutocompleteFromFieldName = useCallback((field: IField) => {
        return (
            {
                firstName: "given-name",
                lastName: "family-name",
                mobilePhone: "tel-national",
                email: "email",
                sexAssignedAtBirth: "sex",
                legalSex: "sex",
                dateOfBirth: "bday",
            }[field.name] || "none"
        );
    }, []);

    const renderField = useCallback(
        (field: IField) => {
            if (isHideFromPatientScheduling(field)) return null;

            if (field.name === "mobilePhone") {
                return (
                    <PhoneFormController
                        name={withFieldPrefix(field)}
                        control={control}
                        defaultValue={getDefaultValue(field)}
                        isInvalid={Boolean(get(errors, withFieldPrefix(field)))}
                        disabled={isChildAccountPatient}
                        autoComplete={getAutocompleteFromFieldName(field)}
                        skipVerify={!!(patientSchedulingEmail || patientSchedulingPhone)}
                    />
                );
                // }
            } else if (field.name === "email") {
                return (
                    <EmailFormController
                        name={withFieldPrefix(field)}
                        control={control}
                        setValue={setValue}
                        defaultValue={getDefaultValue(field)}
                        isInvalid={Boolean(get(errors, withFieldPrefix(field)))}
                        disabled={isChildAccountPatient}
                        placeholder={field.placeholder}
                        isPatient={isPatient}
                        getPatientExistsByEmail={
                            patientSchedulingPhone || patientSchedulingEmail
                                ? undefined
                                : getPatientExistsByEmail
                        }
                        setExistingPatient={setExistingPatient}
                    />
                );
                // }
            } else if (field.type === "date") {
                return (
                    <UseFormTypedDate
                        key={withFieldPrefix(field)}
                        control={control}
                        formField={{
                            ...field,
                            name: withFieldPrefix(field),
                        }}
                        autoComplete={getAutocompleteFromFieldName(field)}
                        required={getFieldRequired(field)}
                    />
                );
            } else if (field.type === "select") {
                if (!field.options) return null;
                return (
                    <LobbieSelectForm
                        control={control}
                        options={[
                            field.defaultOption || { value: -1, label: "Select an option" },
                        ].concat(field.options)}
                        name={withFieldPrefix(field)}
                        id={withFieldPrefix(field)}
                        isMulti={false}
                        isClearable={false}
                        defaultValue={
                            field.defaultOption || { value: -1, label: "Select an option" }
                        }
                        autoComplete={getAutocompleteFromFieldName(field)}
                    />
                );
            } else if (field.type === "checkbox") {
                return (
                    <LobbieCheckboxV2
                        checked={true}
                        onClick={() => setValue(field.name, !getValues(field.name))}
                        label={field.placeholder}
                    />
                );
            } else {
                return (
                    <Form.Control
                        {...register(withFieldPrefix(field))}
                        required={getFieldRequired(field)}
                        type={field.type}
                        placeholder={field.placeholder}
                        defaultValue={getDefaultValue(field)}
                        isInvalid={!!get(errors, withFieldPrefix(field))}
                        autoComplete={getAutocompleteFromFieldName(field)}
                    />
                );
            }
        },
        [
            isHideFromPatientScheduling,
            withFieldPrefix,
            control,
            getDefaultValue,
            errors,
            isChildAccountPatient,
            getAutocompleteFromFieldName,
            patientSchedulingEmail,
            patientSchedulingPhone,
            register,
            getFieldRequired,
            setValue,
            isPatient,
            getPatientExistsByEmail,
            setExistingPatient,
            getValues,
        ],
    );

    return (
        <div className={IS_MOBILE_PHONE ? "" : "border rounded p-3 "}>
            <div className="row">
                {title || (
                    <span
                        className={IS_MOBILE_PHONE ? "extra-bold body-large col" : "bold gray2 col"}
                    >
                        {isRepresentative
                            ? `Representative${(isPatient && " (you)") || ""}`
                            : patientFieldName}{" "}
                        details:
                    </span>
                )}
                {isRepresentative && (
                    <div>
                        <div className="col-6 px-3">
                            <FiX onClick={handleBack} className="gray3 pointer" size="1.5em" />
                        </div>
                    </div>
                )}
            </div>
            <div className="row ">
                {FIELDS.map((field: IField) => {
                    const required = getFieldRequired(field);
                    return (
                        <div
                            key={withFieldPrefix(field)}
                            className={IS_MOBILE_PHONE ? "col-12 mt-3 " : "col-6 mt-3 "}
                        >
                            {field.type !== "date" && getLabel(field, required)}
                            {renderField(field)}
                            <FormFieldError message={getErrorMessage(field)} />
                            <FormFieldError message={getExistingPatientMessage(field)} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default NewPatientFormFields;
